import { useState, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface OverflowChipsProps<T> {
  items: T[];
  max?: number;
  getKey: (item: T) => string;
  renderChip: (item: T) => ReactNode;
  /**
   * How to render the chips inside the `+N` popover, when they need to differ from the
   * inline ones. A caller that clamps a chip to fit a narrow column wants the popover —
   * which has room — to spell the label out in full instead of repeating the clamp.
   */
  renderOverflowChip?: (item: T) => ReactNode;
  ariaLabel?: (hidden: T[]) => string;
  className?: string;
}

export function OverflowChips<T>({
  items,
  max = 3,
  getKey,
  renderChip,
  renderOverflowChip,
  ariaLabel,
  className,
}: OverflowChipsProps<T>) {
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  const visible = items.slice(0, max);
  const hidden = items.slice(max);
  const hasOverflow = hidden.length > 0;

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {visible.map((item) => (
        <span key={getKey(item)}>{renderChip(item)}</span>
      ))}

      {hasOverflow && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label={
                ariaLabel ? ariaLabel(hidden) : `Show ${hidden.length} more`
              }
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs font-normal text-foreground transition-colors hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              +{hidden.length}
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            className="w-auto max-w-xs p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap gap-1">
              {hidden.map((item) => (
                <span key={getKey(item)}>{(renderOverflowChip ?? renderChip)(item)}</span>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
